import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

import RefreshButton from "components/shared/RefreshButton/RefreshButton";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetCostCenterListQuery } from "state/rtkQuery/user/userQueries";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";

import { asList, formatCostCenterDate, formatCredits, getPoolSummary } from "./costCenters/costCenterDisplay";

const ACCENT = "#26890d";
const PER_PAGE = 10;

const headCellSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};
const bodyCellSx = { fontSize: "0.85rem", color: "#1a1a1a", py: 1.2, borderBottom: "1px solid #f0f0f0" };

export const StateChip = ({ state }) => {
  const deleted = state === "deleted";
  return (
    <Chip
      size="small"
      label={deleted ? "Deleted" : "Active"}
      sx={{
        fontWeight: 700,
        fontSize: "0.72rem",
        color: deleted ? "#C53211" : ACCENT,
        backgroundColor: deleted ? "rgba(197,50,17,0.1)" : "rgba(38,137,13,0.1)",
      }}
    />
  );
};

export const PoolBar = ({ cost, compact = false }) => {
  const { current, target, percent } = getPoolSummary(cost);
  if (!cost?.is_pool_enabled) {
    return <Typography sx={{ fontSize: "0.78rem", color: "#999", fontStyle: "italic" }}>Pool not enabled</Typography>;
  }
  if (percent === null) {
    return <Typography sx={{ fontSize: "0.78rem", color: "#999", fontStyle: "italic" }}>No pool data</Typography>;
  }
  return (
    <Box sx={{ minWidth: compact ? 170 : 240 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#1a1a1a" }}>
          {formatCredits(current)} / {formatCredits(target)}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#555" }}>{percent}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(percent, 100)}
        sx={{ height: 5, borderRadius: 4, mt: 0.4, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: ACCENT, borderRadius: 4 } }}
      />
    </Box>
  );
};

// ECR Ops → Cost Centers: every cost center on the selected platform. Click one for its details and users.
const EcrOpsCostCenters = () => {
  const navigate = useNavigate();
  const { platformId } = useAccessPlatformId();
  const [state, setState] = useState("active");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isFetching, isError, refetch } = useGetCostCenterListQuery({ platformId, state }, { skip: !platformId });
  const costCenters = data ?? [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matches = term ? costCenters.filter((cc) => (cc.name ?? "").toLowerCase().includes(term)) : costCenters;
    return [...matches].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [costCenters, search]);

  const nPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const rows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div>
      {/* Shared ECR Ops page header, like every other ops page */}
      <div className="ecr-ops-page-header">
        <p className="ecr-ops-page-header__eyebrow">ECR Ops Portal</p>
        <h1 className="ecr-ops-page-header__title">Cost Centers</h1>
        <p className="ecr-ops-page-header__sub">Every cost center on this platform. Open one to see its details and the users in it.</p>
      </div>

      <div className="ecr-ops-content">
      <Paper elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", p: 2, borderBottom: "1px solid #f0f0f0" }}>
          <TextField
            size="small"
            placeholder="Search cost centers by name"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            inputProps={{ "aria-label": "Search cost centers" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300, "& .MuiInputBase-root": { height: 34, fontSize: "0.85rem" } }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={state}
            onChange={(_, next) => {
              if (next) {
                setState(next);
                setPage(1);
              }
            }}
            aria-label="Cost center state"
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontSize: "0.8rem", px: 1.6, height: 34 } }}
          >
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="deleted">Deleted</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: "0.78rem", color: "#999" }}>
              {filtered.length.toLocaleString("en-AU")} cost center{filtered.length === 1 ? "" : "s"}
            </Typography>
            <RefreshButton label="Refresh cost centers" isRefreshing={isFetching} onClick={refetch} />
          </Box>
        </Box>

        {isFetching && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={26} sx={{ color: ACCENT }} />
          </Box>
        ) : isError ? (
          <Typography sx={{ p: 4, color: "#C53211", textAlign: "center" }}>Couldn't load cost centers. Please refresh and try again.</Typography>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 5, color: "#999", textAlign: "center", fontStyle: "italic" }}>
            {search ? `No ${state} cost center matches “${search.trim()}”.` : `No ${state} cost centers on this platform.`}
          </Typography>
        ) : (
          <TableContainer sx={{ opacity: isFetching ? 0.55 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Cost center</TableCell>
                  <TableCell sx={headCellSx}>State</TableCell>
                  <TableCell sx={headCellSx}>Countries</TableCell>
                  <TableCell sx={headCellSx}>AI credit pool</TableCell>
                  <TableCell sx={headCellSx}>Allocation</TableCell>
                  <TableCell sx={headCellSx}>Last updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((cc) => {
                  const countries = asList(cc.countries);
                  return (
                    <TableRow
                      key={cc._id}
                      hover
                      onClick={() => navigate(`/ecr-ops/cost-centers/${cc._id}`)}
                      sx={{ cursor: "pointer", "&:last-child td": { border: 0 } }}
                    >
                      <TableCell sx={bodyCellSx}>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>{cc.name ?? "—"}</Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <StateChip state={cc.state} />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {countries.length === 0 ? (
                          "—"
                        ) : (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {countries.slice(0, 2).map((country) => (
                              <Chip key={country} size="small" label={country} sx={{ fontSize: "0.72rem", height: 22 }} />
                            ))}
                            {countries.length > 2 && (
                              <Tooltip title={countries.slice(2).join(", ")} arrow>
                                <Chip size="small" label={`+${countries.length - 2}`} sx={{ fontSize: "0.72rem", height: 22 }} />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <PoolBar cost={cc} compact />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Chip
                          size="small"
                          label={cc.is_allocation_disabled ? "Disabled" : "Enabled"}
                          sx={{ fontSize: "0.72rem", color: cc.is_allocation_disabled ? "#C53211" : "#555", backgroundColor: cc.is_allocation_disabled ? "rgba(197,50,17,0.1)" : "#f1f1f1" }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: "#666", fontSize: "0.8rem" }}>{formatCostCenterDate(cc.updated_date)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {nPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 1.5, borderTop: "1px solid #f0f0f0", backgroundColor: "#fafafa" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "#999" }}>
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length.toLocaleString("en-AU")}
            </Typography>
            <Pagination nPages={nPages} currentPage={page} setCurrentPage={setPage} />
          </Box>
        )}
      </Paper>
      </div>
    </div>
  );
};

export default EcrOpsCostCenters;
